'use client';

import { ChevronRight } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Container,
  GridLegacy as Grid,
  Link,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import { useParams } from 'next/navigation';

import LoadingSpinner from '../../../components/atoms/LoadingSpinner';
import NewsCard from '../../../components/molecules/NewsCard';
import { useNewsArticle } from '../../../hooks/useNews';
import { getImageAlt, getImageUrl } from '../../../services/utils/image';

export default function NewsDetailPage() {
  const params = useParams();
  const slug = params.slug as string;

  const { article, sameCategoryArticles, loading, error } =
    useNewsArticle(slug);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth='lg'>
          <LoadingSpinner message='Đang tải bài viết...' />
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography
              variant='h5'
              sx={{
                color: '#ff4444',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 2,
              }}
            >
              Lỗi tải bài viết
            </Typography>
            <Typography
              sx={{
                color: '#666666',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 3,
              }}
            >
              {error}
            </Typography>
            <Link
              href='/news'
              sx={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#EB722E',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                '&:hover': {
                  backgroundColor: '#d65a1a',
                },
              }}
            >
              Quay lại trang tin tức
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography
              variant='h5'
              sx={{
                color: '#666666',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 2,
              }}
            >
              Không tìm thấy bài viết
            </Typography>
            <Typography
              sx={{
                color: '#999999',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                mb: 3,
              }}
            >
              Bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.
            </Typography>
            <Link
              href='/news'
              sx={{
                display: 'inline-block',
                padding: '12px 24px',
                backgroundColor: '#EB722E',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '4px',
                fontFamily:
                  'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                '&:hover': {
                  backgroundColor: '#d65a1a',
                },
              }}
            >
              Quay lại trang tin tức
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <Box sx={{ pt: '80px', pb: '52px' }}>
        <Container maxWidth='lg'>
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Breadcrumbs
              separator={
                <ChevronRight sx={{ color: '#999999', fontSize: 24 }} />
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Link
                href='/'
                underline='none'
                sx={{
                  color: '#999999',
                  fontSize: '16px',
                  fontFamily:
                    'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  '&:hover': {
                    color: '#666666',
                  },
                }}
              >
                Trang chủ
              </Link>
              <Link
                href='/news'
                underline='none'
                sx={{
                  color: '#999999',
                  fontSize: '16px',
                  fontFamily:
                    'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  '&:hover': {
                    color: '#666666',
                  },
                }}
              >
                Tin tức
              </Link>
              {article.category && (
                <Link
                  href={`/news?category=${article.category.slug}`}
                  underline='none'
                  sx={{
                    color: '#999999',
                    fontSize: '16px',
                    fontFamily:
                      'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: 400,
                    lineHeight: 1.5,
                    '&:hover': {
                      color: '#666666',
                    },
                  }}
                >
                  {article.category.name}
                </Link>
              )}
              <Typography
                sx={{
                  color: '#1A1A1A',
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily:
                    'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
                  lineHeight: 1.5,
                }}
              >
                {article.title}
              </Typography>
            </Breadcrumbs>
          </Box>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth='lg' sx={{ py: { xs: 6, md: 8 } }}>
        {/* Main Article - Single Column */}
        <Box sx={{ maxWidth: 1194, mx: 'auto' }}>
          {/* Article Title */}
          <Typography
            variant='h1'
            component='h1'
            sx={{
              fontFamily:
                'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 700,
              fontSize: '32px',
              lineHeight: 1.25,
              color: '#1A1A1A',
              mb: '60px', // Spacing
              width: '100%',
            }}
          >
            {article.title}
          </Typography>

          {/* Article Meta */}
          <Box sx={{ mb: '32px' }}>
            <Typography
              sx={{
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 510,
                fontSize: '14px',
                lineHeight: 1.428,
                color: '#333333',
              }}
            >
              {formatDate(article.publishedDate || article.createdAt)}
            </Typography>
          </Box>

          {/* Article Excerpt */}
          {article.excerpt && (
            <Typography
              sx={{
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#333333',
                mb: '32px',
                width: '100%',
              }}
            >
              {article.excerpt}
            </Typography>
          )}

          {/* Featured Image */}
          {article.featuredImage && (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '511px',
                mb: '32px',
                borderRadius: 0,
                overflow: 'hidden',
              }}
            >
              <Image
                src={getImageUrl(article.featuredImage)}
                alt={getImageAlt(article.featuredImage)}
                fill
                style={{
                  objectFit: 'cover',
                }}
                sizes='1200px'
              />
            </Box>
          )}

          {/* Article Content */}
          <Box
            sx={{
              '& p': {
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#333333',
                mb: '32px',
                textAlign: 'left',
                width: '100%',
              },
              '& h2': {
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: 1.4,
                color: '#1A1A1A',
                mt: '32px',
                mb: '16px',
              },
              '& h3': {
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: 1.4,
                color: '#1A1A1A',
                mt: '24px',
                mb: '16px',
              },
              '& ul, & ol': {
                mb: '32px',
                pl: 3,
              },
              '& li': {
                fontFamily:
                  'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#333333',
                mb: 1,
              },
              '& blockquote': {
                borderLeft: '4px solid #EB722E',
                pl: 3,
                py: 2,
                my: '32px',
                backgroundColor: '#F7F9FC',
                fontStyle: 'italic',
              },
              '& img': {
                width: '100%',
                height: 'auto',
                borderRadius: 0,
                my: '32px',
                display: 'block',
              },
            }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </Box>
      </Container>

      {/* Separator Line */}
      <Box
        sx={{
          width: '100%',
          height: '2px',
          backgroundColor: '#E6E6E6',
          mx: 'auto',
          maxWidth: '1194px',
        }}
      />

      {/* Same Category Articles Section */}
      <Container maxWidth='lg' sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {/* Section Title */}
          <Typography
            variant='h2'
            sx={{
              fontFamily:
                'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 860,
              fontSize: '40px',
              lineHeight: 1.2,
              color: '#EB722E',
              mb: '60px', // Spacing
            }}
          >
            Có thể bạn quan tâm
          </Typography>

          {/* Same Category Articles Grid */}
          {sameCategoryArticles.length > 0 ? (
            <Grid container spacing={3}>
              {sameCategoryArticles.slice(0, 3).map(sameCategoryArticle => (
                <Grid item xs={12} md={4} key={sameCategoryArticle.id}>
                  <NewsCard article={sameCategoryArticle} variant='compact' />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography
                sx={{
                  color: '#999999',
                  fontFamily:
                    'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: '16px',
                  lineHeight: 1.5,
                }}
              >
                Không có tin tức nào khác trong danh mục này
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
